import {
  type PropsWithChildren,
  createContext,
  useContext,
  useState,
} from "react";

import { useDataFormContext } from "../../../data-form";
import { type DataTablePreferencesBrowseType } from "./schema";

export type DataTablePreferencesFormMode =
  | "detail"
  | "delete"
  | "override"
  | "apply";

type DataFormPreferencesContextType = {
  entity: DataTablePreferencesBrowseType | null;
  setEntity: (entity: DataTablePreferencesBrowseType | null) => void;
  mode: DataTablePreferencesFormMode;
  setMode: (mode: DataTablePreferencesFormMode) => void;
};

const DataFormPreferencesContext =
  createContext<DataFormPreferencesContextType | null>(null);

function useDataFormPreferencesContextRaw() {
  const context = useContext(DataFormPreferencesContext);

  if (!context) {
    throw new Error(
      "useDataFormPreferencesContext must be used within a DataFormPreferencesProvider",
    );
  }

  return context;
}

/**
 * Hook for reading preferences state outside DataForm (e.g., grid layout toggle).
 */
export function useDataFormPreferences() {
  return useDataFormPreferencesContextRaw();
}

export function DataFormPreferencesProvider({ children }: PropsWithChildren) {
  const [entity, setEntity] = useState<DataTablePreferencesBrowseType | null>(
    null,
  );
  const [mode, setMode] = useState<DataTablePreferencesFormMode>("detail");

  return (
    <DataFormPreferencesContext.Provider
      value={{ entity, setEntity, mode, setMode }}
    >
      {children}
    </DataFormPreferencesContext.Provider>
  );
}

export function useDataFormPreferencesContext() {
  const preferences = useDataFormPreferencesContextRaw();
  const dataForm = useDataFormContext<
    DataTablePreferencesBrowseType,
    DataTablePreferencesBrowseType
  >();

  return {
    ...dataForm,
    ...preferences,
    formMode: dataForm.mode,
    setFormMode: dataForm.setMode,
  };
}
